import React from 'react';
import { useNode } from '@craftjs/core';
import { PROP_POSICION, normalizarPosicion } from '../utils/posicionLibre';

type Proporcion = '16:9' | '4:3' | '1:1' | '21:9';

interface VideoPlayerProps {
  videoUrl?: string;
  aspectRatio?: Proporcion;
  maxWidth?: number;
  borderRadius?: number;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  align?: 'left' | 'center' | 'right';
  margin?: number;
  /** Sacado de la pagina publicada sin borrarlo. En el editor sigue, tenue. */
  oculto?: boolean;
}

export const PROPORCIONES: Record<Proporcion, string> = {
  '16:9': '56.25%',
  '4:3': '75%',
  '1:1': '100%',
  '21:9': '42.85%',
};

/**
 * Devuelve la URL embebible de YouTube/Vimeo, o null si es un archivo directo
 * (mp4/webm) que se pinta con `<video>`.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const urlEmbebible = (url: string, opciones: { autoplay?: boolean; muted?: boolean; loop?: boolean } = {}) => {
  if (!url) return null;
  let id: string | undefined;
  let base: string | null = null;

  if (url.includes('youtube.com/watch')) {
    id = url.split('v=')[1]?.split('&')[0];
    base = id ? `https://www.youtube.com/embed/${id}` : null;
  } else if (url.includes('youtu.be/')) {
    id = url.split('youtu.be/')[1]?.split('?')[0];
    base = id ? `https://www.youtube.com/embed/${id}` : null;
  } else if (url.includes('youtube.com/embed/')) {
    base = url.split('?')[0];
    id = base.split('/embed/')[1];
  } else if (url.includes('vimeo.com/')) {
    id = url.split('vimeo.com/')[1]?.split('?')[0];
    base = id ? `https://player.vimeo.com/video/${id}` : null;
  }
  if (!base) return null;

  const params: string[] = [];
  if (opciones.autoplay) params.push('autoplay=1');
  // Los navegadores solo dejan autoreproducir sin sonido.
  if (opciones.muted || opciones.autoplay) params.push('muted=1', 'mute=1');
  if (opciones.loop) {
    params.push('loop=1');
    if (id && base.includes('youtube')) params.push(`playlist=${id}`);
  }
  return params.length ? `${base}?${params.join('&')}` : base;
};

const esArchivo = (url: string) => /\.(mp4|webm|ogg)(\?|$)/i.test(url);

/**
 * Reproductor de video suelto — render del sitio público.
 *
 * A diferencia de `VideoEmbed`, que es una sección entera con título y fondo,
 * esto es solo el reproductor: se suelta dentro de una `Section`, un
 * `Container` o una `Grid` y se combina con lo que haga falta.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/VideoPlayer.tsx.
 */
export const VideoPlayer = ({
  videoUrl = '',
  aspectRatio = '16:9',
  maxWidth = 0,
  borderRadius = 8,
  autoplay = false,
  muted = false,
  loop = false,
  align = 'center',
  margin = 0,
  oculto = false,
}: VideoPlayerProps) => {
  const { connectors, enLienzo } = useNode((state) => ({
    enLienzo: !!normalizarPosicion((state.data.props as Record<string, unknown>)?.[PROP_POSICION]),
  }));

  // Ocultarlo es no dibujarlo; en el editor sigue estando, tenue.
  if (oculto) return null;

  const ancho = Math.max(0, Number(maxWidth) || 0);
  const embed = urlEmbebible(videoUrl, { autoplay, muted, loop });
  const archivo = !embed && esArchivo(videoUrl);

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{
        width: '100%',
        margin: `${margin}px 0`,
        display: 'flex',
        justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
        // En un lienzo la caja es el frame que eligió el dealer, no la que
        // deduce la proporción: el editor la dibuja así y el sitio tiene que
        // coincidir.
        ...(enLienzo ? { height: '100%', margin: 0, display: 'block' } : null),
      }}
    >
      <div style={{ width: '100%', ...(enLienzo ? { height: '100%' } : ancho > 0 ? { maxWidth: `${ancho}px` } : {}) }}>
        <div
          data-video-marco=""
          style={{
            position: 'relative',
            width: '100%',
            // Fuera del lienzo el alto lo da la proporción; adentro, el frame.
            ...(enLienzo
              ? { height: '100%' }
              : { paddingTop: PROPORCIONES[aspectRatio] ?? PROPORCIONES['16:9'] }),
            borderRadius: `${borderRadius}px`,
            overflow: 'hidden',
            background: '#000',
          }}
        >
          {embed && (
            <iframe
              src={embed}
              title="Video"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          {archivo && (
            <video
              src={videoUrl}
              controls
              autoPlay={autoplay}
              muted={muted || autoplay}
              loop={loop}
              playsInline
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

VideoPlayer.craft = {
  displayName: 'VideoPlayer',
  props: {
    videoUrl: '',
    aspectRatio: '16:9',
    maxWidth: 0,
    borderRadius: 8,
    autoplay: false,
    muted: false,
    loop: false,
    align: 'center',
    margin: 0,
  },
};
